import React from 'react';
import { Document, Page, View, Text as PdfText, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfFilterLine from '../components/PdfFilterLine';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 40, marginTop: 12 },
  acctName: { fontSize: 11, fontWeight: 'bold', marginBottom: 2 },
  acctCode: { fontSize: 8, marginBottom: 10 },
  grpRow:  { flexDirection: 'row', paddingHorizontal: 4, paddingVertical: 4 },
  grpText: { fontSize: 8.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.4 },
});

const ALL_COLS = [
  { label: 'Account',     flex: 2.4 },
  { label: 'Code',        flex: 0.8 },
  { label: 'Sub-Group',   flex: 1.2 },
  { label: 'Opening Bal', flex: 1.2, align: 'right' },
  { label: 'Total Dr',    flex: 1.0, align: 'right' },
  { label: 'Total Cr',    flex: 1.0, align: 'right' },
  { label: 'Closing Bal', flex: 1.2, align: 'right' },
];

const SINGLE_COLS = [
  { label: 'Date',           flex: 0.9 },
  { label: 'Voucher No',     flex: 1.0 },
  { label: 'Payee / Member', flex: 1.2 },
  { label: 'Narration',      flex: 2.4 },
  { label: 'Dr (Rs.)',       flex: 1.0, align: 'right' },
  { label: 'Cr (Rs.)',       flex: 1.0, align: 'right' },
  { label: 'Balance',        flex: 1.2, align: 'right' },
];

const fmtBal = (value, normalBalance) => {
  const abs = Math.abs(value ?? 0);
  if (abs < 0.005) return '-';
  const isNormal = (value ?? 0) >= 0;
  const label = isNormal ? normalBalance : (normalBalance === 'Dr' ? 'Cr' : 'Dr');
  return `${fmtINR(abs)} ${label}`;
};

export default function LedgerAccountsDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];

  /* ── Single-account view ─────────────────────────────────────────────── */
  if (data.mode === 'single') {
    const rows = Array.isArray(data.rows) ? data.rows : [];
    return (
      <Document>
        <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
          {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Ledger Balance" theme={t} />}
          <View style={s.content}>
            {sections.filterLine && data.filters && <PdfFilterLine filters={data.filters} theme={t} />}
            <View style={{ marginBottom: 8 }}>
              <PdfText style={[s.acctName, { color: t.body }]}>{data.accountName || ''}</PdfText>
              {data.accountCode ? (
                <PdfText style={[s.acctCode, { color: t.muted }]}>Code: {data.accountCode}</PdfText>
              ) : null}
            </View>
            <PdfTableHead cols={SINGLE_COLS} theme={t} />
            <PdfTableRow cols={SINGLE_COLS} index={0} theme={t}
              values={['Opening Balance', '', '', '', '', '', fmtBal(data.openingBalance, data.normalBalance)]}
            />
            {rows.map((r, i) => (
              <PdfTableRow key={i} cols={SINGLE_COLS} index={i + 1} theme={t}
                values={[
                  r.entryDate   || '-',
                  r.voucherNo   || '-',
                  r.payeeName   || '-',
                  r.narration   || '-',
                  r.dr > 0 ? fmtINR(r.dr) : '-',
                  r.cr > 0 ? fmtINR(r.cr) : '-',
                  fmtBal(r.runningBalance, data.normalBalance),
                ]}
              />
            ))}
            <PdfTableFoot cols={SINGLE_COLS}
              values={['Closing Balance', '', '', '', '', '', fmtBal(data.closingBalance, data.normalBalance)]}
              theme={t}
            />
          </View>
          {sections.footer && <PdfFooter theme={t} />}
        </Page>
      </Document>
    );
  }

  /* ── All-ledgers view ────────────────────────────────────────────────── */
  const groups = Array.isArray(data.groups) ? data.groups : [];
  const allRows = groups.flatMap((g) => g.rows || []);
  const grandDr = allRows.reduce((s, r) => s + (r.totalDr || 0), 0);
  const grandCr = allRows.reduce((s, r) => s + (r.totalCr || 0), 0);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Ledger Balance - All Accounts" theme={t} />}
        <View style={s.content}>
          {sections.filterLine && data.filters && <PdfFilterLine filters={data.filters} theme={t} />}
          <PdfTableHead cols={ALL_COLS} theme={t} />
          {groups.map((group, gi) => (
            <React.Fragment key={gi}>
              <View style={[s.grpRow, { backgroundColor: t.light }]} wrap={false}>
                <PdfText style={[s.grpText, { color: t.primary }]}>{group.name || ''}</PdfText>
              </View>
              {(group.rows || []).map((row, i) => (
                <PdfTableRow key={i} cols={ALL_COLS} index={i} theme={t}
                  values={[
                    row.accountName  || '-',
                    row.accountCode  || '-',
                    row.subGroupName || '-',
                    fmtBal(row.openingBalance, row.normalBalance),
                    row.totalDr > 0 ? fmtINR(row.totalDr) : '-',
                    row.totalCr > 0 ? fmtINR(row.totalCr) : '-',
                    fmtBal(row.closingBalance, row.normalBalance),
                  ]}
                />
              ))}
            </React.Fragment>
          ))}
          <PdfTableFoot cols={ALL_COLS}
            values={['Grand Total', '', '', '', fmtINR(grandDr), fmtINR(grandCr), '']}
            theme={t}
          />
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
